import { useIntl } from 'react-intl';

import Button from 'src/componentes/button';
import { Checkbox } from 'src/componentes/Form';
import LoadingContainer from 'src/componentes/loading-container';
import MapaCalor from 'src/componentes/mapa-calor';
import Spinner from 'src/componentes/spinner';
import useAuth from 'src/hooks/useAuth';
import { capitalizeFirstLetter } from 'src/utils/string';
import { getLikertHeatMapRange } from './likertHelpers';
import useDashboardMapaCalorLikert from './useDashboardMapaCalorLikert';

const NEGATIVE_PATTERN =
  'repeating-linear-gradient(135deg, transparent 0, transparent 4px, rgba(84, 27, 23, 0.12) 4px, rgba(84, 27, 23, 0.12) 6px)';

const RANGE_STYLES = {
  positiva: {
    backgroundColor: '#9fce93',
    borderColor: '#9bc790',
    color: '#1f3b1c',
  },
  neutra: {
    backgroundColor: '#f8c572',
    borderColor: '#e7b958',
    color: '#4a3200',
  },
  negativa: {
    backgroundColor: '#e6a19b',
    backgroundImage: NEGATIVE_PATTERN,
    borderColor: '#d98780',
    color: '#541b17',
  },
};

const styles = {
  heading: {
    marginBottom: 14,
    marginTop: 18,
  },
  title: {
    color: '#202124',
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 4,
  },
  subtitle: {
    color: '#7b7f87',
    fontSize: 13,
  },
  consolidationOption: {
    alignItems: 'center',
    color: '#344054',
    display: 'flex',
    fontSize: 13,
    gap: 10,
    marginTop: 12,
  },
  state: {
    alignItems: 'center',
    color: '#667085',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    justifyContent: 'center',
    minHeight: 180,
    padding: '32px 16px',
    textAlign: 'center',
  },
  loadingContainer: {
    minHeight: 180,
  },
  loadMore: {
    alignItems: 'center',
    display: 'flex',
    minHeight: 42,
  },
};

const toNullableNumber = (value) => {
  if (value == null || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeArea = (area, areaIndex) => {
  const cells = (area.resultados || []).reduce((result, current) => {
    if (current.dimensaoId == null) return result;

    result[String(current.dimensaoId)] = {
      validResponseCount: Number(current.totalRespostasValidas || 0),
      value: toNullableNumber(current.resultadoPercentual),
    };

    return result;
  }, {});

  return {
    average: {
      validResponseCount: Number(area.totalRespostasValidas || 0),
      value: toNullableNumber(area.mediaGeralPercentual),
    },
    cells,
    hasChildren: area.possuiFilhos === true,
    id: area.areaId ?? areaIndex,
    label: area.areaNome || '',
    level: Number(area.nivel || 0),
    parentId: area.areaPaiId,
  };
};

function LikertMapaCalor({ entidadeId, filtros, refreshToken }) {
  const intl = useIntl();
  const { terms: resource } = useAuth();
  const {
    changeConsolidation,
    changeRootPage,
    columns,
    consolidaAreasSubordinadas,
    erro,
    isLoading,
    loadMoreChildren,
    paginationCount,
    paginationCurrentPage,
    paginationPageSize,
    retryRoot,
    rows,
    toggleRow,
  } = useDashboardMapaCalorLikert(entidadeId, {
    filtros,
    normalizeArea,
    refreshToken,
  });

  const ranges = {
    positiva: {
      ...RANGE_STYLES.positiva,
      statusLabel: intl.formatMessage({
        id: 'labelLikertPositiva',
      }),
    },
    neutra: {
      ...RANGE_STYLES.neutra,
      statusLabel: intl.formatMessage({
        id: 'labelLikertNeutra',
      }),
    },
    negativa: {
      ...RANGE_STYLES.negativa,
      statusLabel: intl.formatMessage({
        id: 'labelLikertNegativa',
      }),
    },
  };

  const formatValue = (value) =>
    `${intl.formatNumber(Number(value), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%`;

  const getCellAppearance = (value) =>
    ranges[getLikertHeatMapRange(value)] || {};

  const getValidResponsesLabel = (count) => {
    const resourceId =
      Number(count) === 1
        ? 'labelQuantidadeRespostaValida'
        : 'labelQuantidadeRespostasValidas';

    return intl.formatMessage({ id: resourceId }, { count });
  };

  const formatCellAriaLabel = ({
    appearance,
    cell,
    column,
    formattedValue,
    hasValue,
    row,
  }) => {
    if (!hasValue) {
      return intl.formatMessage(
        {
          id: 'labelMapaCalorCelulaSemDados',
        },
        {
          area: row.label,
          dimensao: column.label,
        }
      );
    }

    return intl.formatMessage(
      {
        id: 'labelMapaCalorCelulaDescricao',
      },
      {
        area: row.label,
        dimensao: column.label,
        faixa: appearance.statusLabel,
        respostas: getValidResponsesLabel(cell.validResponseCount || 0),
        valor: formattedValue,
      }
    );
  };

  const renderState = (message, action) => (
    <div style={styles.state}>
      <span>{message}</span>
      {action}
    </div>
  );

  return (
    <div>
      <div style={styles.heading}>
        <div style={styles.title}>
          {intl.formatMessage({
            id: 'labelMapaCalorLikert',
          })}
        </div>
        <div style={styles.subtitle}>
          {intl.formatMessage({
            id: 'labelMapaCalorLikertDescricao',
          })}
        </div>
        <div style={styles.consolidationOption}>
          <Checkbox
            checked={consolidaAreasSubordinadas}
            id="switch-consolida-areas-subordinadas-mapa-calor-likert"
            label={intl.formatMessage(
              { id: 'consolidaAreasSubordinadas' },
              { areas: resource.areas }
            )}
            name="consolidaAreasSubordinadasMapaCalorLikert"
            onChange={(event) =>
              changeConsolidation(event.target?.checked === true)
            }
            type="switch"
          />
        </div>
      </div>

      <LoadingContainer isLoading={isLoading} style={styles.loadingContainer}>
        {!isLoading &&
          erro &&
          renderState(
            intl.formatMessage({
              id: 'labelMapaCalorErroCarregamento',
            }),
            <Button color="secondary" onClick={retryRoot}>
              {intl.formatMessage({
                id: 'labelTentarNovamente',
              })}
            </Button>
          )}

        {!isLoading &&
          !erro &&
          (columns.length === 0 || rows.length === 0) &&
          renderState(
            intl.formatMessage({
              id: 'labelMapaCalorLikertSemDados',
            })
          )}

        {!isLoading && !erro && columns.length > 0 && rows.length > 0 && (
          <MapaCalor
            ariaLabel={intl.formatMessage({
              id: 'labelMapaCalorLikert',
            })}
            averageLabel={intl.formatMessage({
              id: 'labelLikertMediaGeral',
            })}
            columns={columns}
            formatCellAriaLabel={formatCellAriaLabel}
            formatValue={formatValue}
            getCellAppearance={getCellAppearance}
            getCellTitle={({ cell, formattedValue, hasValue }) =>
              hasValue
                ? `${formattedValue} • ${getValidResponsesLabel(
                    cell.validResponseCount || 0
                  )}`
                : intl.formatMessage({
                    id: 'labelSemDados',
                  })
            }
            getRowToggleLabel={(row, isExpanded) =>
              `${intl.formatMessage({
                id: isExpanded ? 'colapsar' : 'expandir',
              })}: ${row.label}`
            }
            hierarchicalRows
            legend={['positiva', 'neutra', 'negativa'].map((range) => ({
              id: range,
              label: ranges[range].statusLabel,
              ...RANGE_STYLES[range],
            }))}
            onChangePage={changeRootPage}
            onToggleRow={toggleRow}
            paginationCount={paginationCount}
            paginationCurrentPage={paginationCurrentPage}
            paginationPageSize={paginationPageSize}
            renderCustomRow={(row) => (
              <div
                style={{
                  ...styles.loadMore,
                  paddingLeft: `${row.level * 16 + 26}px`,
                }}
              >
                <Button
                  color="secondary"
                  disabled={row.isLoading}
                  onClick={() => loadMoreChildren(row)}
                  aria-busy={row.isLoading}
                  leftIcon={row.isLoading ? <Spinner aria-hidden /> : undefined}
                >
                  {intl.formatMessage({ id: 'labelCarregarMais' })}
                </Button>
              </div>
            )}
            rowHeaderLabel={capitalizeFirstLetter(resource.area)}
            rows={rows}
          />
        )}
      </LoadingContainer>
    </div>
  );
}

export default LikertMapaCalor;
